import { useState, useEffect, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { useNavigate, useParams } from "react-router-dom";
import {
  Users,
  FileText,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ImageUpscale,
  Copy,
} from "lucide-react";
import { GET, POST } from "../../service/CRUDService";
import CfLoader from "../../common/CfLoader/CfLoader";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import useNavigationStore from "../../store/navigationStore";
import QrEditModal from "../../common/QrEditModal/QrEditModal";

export default function IssuanceSummaryPage() {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isQrEditModalOpen, setIsQrEditModalOpen] = useState(false);
  const [previewRecipient, setPreviewRecipient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const params = useParams();
  const navigate = useNavigate();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  useEffect(() => {
    if (!currentProgramme || !currentBatch) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [currentProgramme, currentBatch, navigate]);

  const [qrPosition, setQrPosition] = useState({ x: 50, y: 50 });
  const [qrSize, setQrSize] = useState({ width: 110, height: 110 });

  const certificateRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const handlePreviewCertificate = async (recipient) => {
    if (!recipient.is_legacy_file_present) return;

    setPreviewRecipient(recipient);
    setIsPreviewModalOpen(true);

    if (recipient.legacy_file_url) {
      try {
        const loadingTask = pdfjsLib.getDocument(recipient.legacy_file_url);
        const pdf = await loadingTask.promise;

        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.0 });

        const canvas = pdfCanvasRef.current;
        const context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
      } catch (error) {
        console.error("Error rendering PDF:", error);
      }
    }
  };

  const handleOpenQrEditModal = () => {
    setIsQrEditModalOpen(true);
  };

  const handleIssueCertificates = async () => {
    setIsConfirmModalOpen(false);
    try {
      setLoading(true);

      const payload = {
        data: summaryData.validated_data
          .filter(
            (recipient) =>
              recipient.is_legacy_file_present &&
              recipient.is_duplicate !== true
          )
          .map((recipient) => {
            const recipientData = {};

            Object.keys(recipient).forEach((key) => {
              if (
                key !== "is_legacy_file_present" &&
                key !== "file_name" &&
                key !== "legacy_file_url"
              ) {
                recipientData[key] = recipient[key];
              }
            });

            recipientData.legacy_document = recipient.legacy_file_url;

            return recipientData;
          }),
      };

      const response = await POST(
        `${EntityConstants.POST_LEGACY_ISSUANCE}?credential_template=${params.issuer_credential_template_id}&legacy_transaction_id=${params.legacyTransactionId}`,
        payload
      );

      const data = await response.json();

      if (response.ok) {
        navigate(
          `/issuer/workspace/batches/summary/${data.credential_template_id}/${data.issuer_credential_template_id}?templateType=${data.template_type}`,
          {
            state: {
              transaction_id: data.transaction_id,
              programme: data.programme_name,
              batch: data.batch_name,
            },
          }
        );
      } else {
        throw new Error(data.message || "Failed to issue certificates");
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Error",
        message: error.message,
      });
    } finally {
      setLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        const response = await GET(
          `${EntityConstants.GET_VIEW_SUMMARY}?legacy_transction_log_id=${params.legacyTransactionId}`
        );

        const data = await response.json();

        if (response.ok) {
          setSummaryData(data);
        } else {
          throw new Error(data.message || "Failed to fetch summary data");
        }
      } catch (error) {
        Toast({
          type: "error",
          title: "Error",
          message: error.message,
        });
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchSummaryData();
  }, [params.legacyTransactionId, navigate]);

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace`);
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handleTemplatesClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };

  const handlePendingPageClick = () => {
    const credentialTemplateId =
      summaryData?.legacy_transcation_data?.credential_template_id;
    const issuerCredentialTemplateId =
      summaryData?.legacy_transcation_data?.issuer_credential_template_id;

    if (credentialTemplateId && issuerCredentialTemplateId) {
      const navigationUrl = `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}?templateType=Legacy`;
      navigate(navigationUrl);
    } else {
      navigate(-1);
    }
  };

  const handleKeyDown = (event, handler) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handler();
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = summaryData?.validated_data?.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(
    (summaryData?.validated_data?.length || 0) / itemsPerPage
  );

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const firstValidPdfUrl = summaryData?.validated_data?.find(
    (recipient) => recipient.is_legacy_file_present
  )?.legacy_file_url;

  if (!currentProgramme || !currentBatch || loading) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <CfLoader />
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <div className="bg-cfSecondary p-6 rounded-lg shadow-md">
          <p className="text-lg text-gray-700">No summary data available</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-indigo-600 text-cfSecondary rounded-md hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10">
      <div className="">
        <div
          className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
        >
          <div
            className={`flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full`}
          >
            <div className="">
              <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
                <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                  <div className="flex items-center gap-2 text-sm text-cfTextBreadcrumbDisabled">
                    <button
                      onClick={handleProgrammeClick}
                      onKeyDown={(e) => handleKeyDown(e, handleProgrammeClick)}
                      className="hover:text-cfTextBreadcrumbActive cursor-pointer"
                      tabIndex={0}
                      aria-label={`Navigate to ${
                        currentProgramme?.programme_name || "Programme"
                      }`}
                    >
                      {currentProgramme?.programme_name || "Programme"}
                    </button>
                    <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                    <button
                      onClick={handleBatchClick}
                      onKeyDown={(e) => handleKeyDown(e, handleBatchClick)}
                      className="hover:text-cfTextBreadcrumbActive cursor-pointer"
                      tabIndex={0}
                      aria-label={`Navigate to ${
                        currentBatch?.batch_name || "Batch"
                      }`}
                    >
                      {currentBatch?.batch_name || "Batch"}
                    </button>
                    <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                    <button
                      onClick={handleTemplatesClick}
                      onKeyDown={(e) => handleKeyDown(e, handleTemplatesClick)}
                      className="hover:text-cfTextBreadcrumbActive cursor-pointer"
                      tabIndex={0}
                      aria-label="Navigate to Certificates"
                    >
                      Certificates
                    </button>
                    <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                    <button
                      onClick={handlePendingPageClick}
                      onKeyDown={(e) =>
                        handleKeyDown(e, handlePendingPageClick)
                      }
                      className="hover:text-cfTextBreadcrumbActive cursor-pointer"
                      tabIndex={0}
                      aria-label="Navigate to Summary"
                    >
                      Summary
                    </button>
                    <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                    <span className="text-cfTextBreadcrumbActive font-medium">
                      Issuance Summary
                    </span>
                  </div>
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <div>
              <h1 className="text-xl font-bold text-cfHeadingText">
                Issuance Summary
              </h1>
            </div>
          </div>
          <div className="gap-3 flex">
            <button
              className="submit-btn px-4 py-2 text-black border items-center text-center text-md rounded-[8px] focus:outline-none"
              onClick={handleOpenQrEditModal}
              disabled={!firstValidPdfUrl}
            >
              Preview Certificate
            </button>
            <button
              className="submit-btn px-4 py-2 bg-cfPrimaryButton text-cfSecondary text-md rounded-[8px] focus:outline-none"
              onClick={() => setIsConfirmModalOpen(true)}
            >
              Issue Certificates
            </button>
          </div>
        </div>

        {/* Confirmation Modal */}
        {isConfirmModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <button
              className="absolute inset-0 w-full h-full cursor-default"
              onClick={() => setIsConfirmModalOpen(false)}
              onKeyDown={(e) =>
                handleKeyDown(e, () => setIsConfirmModalOpen(false))
              }
              aria-label="Close confirmation modal"
            />
            <div
              className="bg-white rounded-xl max-w-lg w-full transform transition-all duration-300 scale-95 opacity-0 animate-modalOpen relative z-10"
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-modal-title"
            >
              <div className="p-4 border-b">
                <h3 id="confirm-modal-title" className="text-lg font-semibold">
                  Confirm Certificate Issuance
                </h3>
                <p className="text-sm text-gray-500">
                  {summaryData.metrics.count_of_valid_recipient > 0 && (
                    <span className="font-medium">
                      You are about to issue{" "}
                      {summaryData.metrics.count_of_valid_recipients}{" "}
                      certificates.
                    </span>
                  )}
                  <br />
                  {summaryData.metrics.count_of_duplicate_values > 0 && (
                    <div>
                      <span className="font-semibold text-amber-600">
                        Warning: {summaryData.metrics.count_of_duplicate_values}{" "}
                        of these have already been issued and will not be issued
                        again.
                      </span>
                      <br />
                      <span>
                        This action is irreversible. Please confirm to proceed.
                      </span>
                    </div>
                  )}
                </p>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 text-amber-600 mb-2">
                  <AlertCircle className="h-5 w-5" />
                  <p className="font-medium">Important Information</p>
                </div>
                <ul className="text-sm text-gray-500 space-y-1 list-disc pl-5">
                  <li>Certificates will be issued to all valid recipients</li>
                  <li>
                    Email notifications will be sent to recipients if enabled
                  </li>
                  <li>Duplicate recipients will not receive certificates</li>
                </ul>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <button
                  className="px-4 py-2 w-[7rem] border border-gray-300 rounded-md hover:bg-gray-50"
                  onClick={() => setIsConfirmModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 w-[7rem] bg-[#3538CD] text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleIssueCertificates}
                  disabled={summaryData.metrics.count_of_valid_recipients === 0}
                >
                  Issue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Preview Modal */}
        {isPreviewModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 transition-opacity duration-300">
            <button
              className="absolute inset-0 w-full h-full cursor-default"
              onClick={() => setIsPreviewModalOpen(false)}
              onKeyDown={(e) =>
                handleKeyDown(e, () => setIsPreviewModalOpen(false))
              }
              aria-label="Close preview modal"
            />
            <div
              className="bg-white rounded-xl max-w-3xl w-full h-[90vh] flex flex-col scale-100 opacity-100 translate-y-0 transform transition-all duration-300 animate-modalOpen relative z-10"
              role="dialog"
              aria-modal="true"
              aria-labelledby="preview-modal-title"
            >
              <div className="p-4 border-b">
                <h3 id="preview-modal-title" className="text-lg font-semibold">
                  Certificate Preview
                </h3>
                {previewRecipient && (
                  <p className="text-sm text-gray-500">
                    Previewing certificate for{" "}
                    {previewRecipient.recipientFirstName}{" "}
                    {previewRecipient.recipientLastName}
                  </p>
                )}
              </div>
              <div className="flex-1 p-4 overflow-auto">
                {previewRecipient && previewRecipient.legacy_file_url ? (
                  <div className="h-full w-full flex items-center justify-center">
                    <canvas
                      ref={pdfCanvasRef}
                      className="border rounded-md shadow-lg max-w-full max-h-full"
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center bg-gray-50 rounded-md">
                    <p className="text-gray-500">No preview available</p>
                  </div>
                )}
              </div>
              <div className="p-4 border-t flex justify-end">
                <button
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  onClick={() => setIsPreviewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {isQrEditModalOpen && (
          <QrEditModal
            isOpen={isQrEditModalOpen}
            onClose={() => setIsQrEditModalOpen(false)}
            qrPosition={qrPosition}
            setQrPosition={setQrPosition}
            qrSize={qrSize}
            setQrSize={setQrSize}
            certificateRef={certificateRef}
            pdfUrl={firstValidPdfUrl}
            issuer_credential_template_id={params.issuer_credential_template_id}
          />
        )}
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 px-4">
          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Recipients</p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.number_of_rows_in_excel}
                </h3>
              </div>
              <div className="bg-indigo-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Valid Recipients</p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.count_of_valid_recipients}
                </h3>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  Duplicate Recipients
                </p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.count_of_duplicate_values}
                </h3>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <Copy className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Certificate Files</p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.count_of_recipient_files}
                </h3>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Issuance Details */}
        <div className="bg-white border rounded-xl p-6 m-6 mb-6">
          <h2 className="text-lg text-cfSignInSignUpHeadingText font-semibold mb-4">
            Issuance Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Issuance Name</p>
              <p className="font-medium">
                {summaryData.legacy_transcation_data.transaction_name}
              </p>
            </div>
            {summaryData.legacy_transcation_data.description && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p>{summaryData.legacy_transcation_data.description}</p>
              </div>
            )}

            <div>
              <p className="text-sm text-gray-500 mb-1">Common Column</p>
              <p className="font-medium">
                {summaryData.legacy_transcation_data.common_column}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Template</p>
              <p className="font-medium">
                {summaryData.legacy_transcation_data.certificate_name}
              </p>
            </div>
          </div>
        </div>

        {/* Recipients Table */}
        <div className="bg-white border rounded-xl overflow-hidden m-4">
          <div className="p-4 border-b">
            <h2 className="text-lg text-cfSignInSignUpHeadingText font-semibold">
              Recipients
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#e0eaff] text-center h-[3.625rem]">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-cfSignInSignUpInputLabelText uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-cfSignInSignUpInputLabelText uppercase tracking-wider">
                    Action
                  </th>
                  {Object.keys(summaryData.validated_data[0]).map((key) => {
                    if (
                      key === "is_legacy_file_present" ||
                      key === "legacy_file_url" ||
                      key === "is_duplicate"
                    )
                      return null;
                    const headerText = summaryData.legacy_transcation_data
                      .is_nad
                      ? key.replace(/_/g, " ")
                      : key
                          .replace(/_/g, " ")
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase());
                    return (
                      <th
                        key={key}
                        className="px-6 py-3 text-xs font-semibold text-cfSignInSignUpInputLabelText uppercase tracking-wider"
                      >
                        <div
                          className="truncate max-w-[150px] mx-auto"
                          title={headerText}
                        >
                          {headerText}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-center">
                {currentItems.map((recipient, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 h-[3.625rem] mx-4"
                  >
                    <td className="px-4 whitespace-nowrap text-sm">
                      {!recipient.is_duplicate &&
                        recipient.is_legacy_file_present && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" /> Valid
                          </span>
                        )}
                      {((recipient.is_duplicate &&
                        recipient.is_legacy_file_present) ||
                        (recipient.is_duplicate &&
                          !recipient.is_legacy_file_present)) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertCircle className="h-3 w-3 mr-1" /> Duplicate
                        </span>
                      )}
                      {!recipient.is_duplicate &&
                        !recipient.is_legacy_file_present && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="h-3 w-3 mr-1" /> Unmatched
                          </span>
                        )}
                    </td>
                    <td className="px-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handlePreviewCertificate(recipient)}
                        className={`inline-flex items-center justify-center p-2 rounded-full focus:outline-none ${
                          recipient.is_legacy_file_present
                            ? "text-indigo-600 hover:bg-indigo-50 cursor-pointer"
                            : "text-gray-300 cursor-not-allowed"
                        }`}
                        disabled={!recipient.is_legacy_file_present}
                        title={
                          recipient.is_legacy_file_present
                            ? "Preview Certificate"
                            : "No certificate to preview"
                        }
                        aria-label={
                          recipient.is_legacy_file_present
                            ? `Preview certificate for ${recipient.recipientFirstName} ${recipient.recipientLastName}`
                            : "Certificate preview not available"
                        }
                      >
                        <ImageUpscale className="h-5 w-5" />
                      </button>
                    </td>
                    {Object.entries(recipient).map(([key, value]) => {
                      if (
                        key === "is_legacy_file_present" ||
                        key === "legacy_file_url" ||
                        key === "is_duplicate"
                      )
                        return null;
                      return (
                        <td
                          key={key}
                          className="px-4 whitespace-nowrap text-sm text-cfSignInSignUpInputLabelText"
                        >
                          {value || "-"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 px-4 py-3 border-t">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            >
              <span className="flex items-center justify-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                  />
                </svg>
                <span>Previous</span>
              </span>
            </button>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            >
              <span className="flex items-center justify-center gap-2">
                <span>Next</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
